import imageCompression from 'browser-image-compression';

// Downscales a photo before it is queued for upload.
//
// Field agents are on low-end Android over 2G/3G. A result sheet straight off the camera
// is 3-5MB; at 1600px on the long edge it is still legible and lands around 300-600KB.
// That is the difference between a 90-second upload and a 9-second one, and it is why
// there is no "send original quality" affordance — there is no legitimate reason to push
// a 4MB original over a rural cell link.
//
// browser-image-compression has been a dependency since the app was scaffolded and was
// never imported. This is the first caller.

export const COMPRESSION_OPTIONS = {
	maxWidthOrHeight: 1600,
	maxSizeMB: 0.6,
	useWebWorker: true,
} as const;

/**
 * Below this, compressing costs more than it saves and can even grow the file.
 * Mirrors the `compressThreshold` the admin app's useCloudinaryUpload uses.
 */
export const COMPRESS_THRESHOLD_BYTES = 200 * 1024;

/** Long edge of the stored thumbnail. ~4KB at this size, small enough for localStorage. */
export const THUMBNAIL_MAX_PX = 120;

/**
 * Compress if worthwhile, otherwise hand back the original.
 *
 * Never throws. A compression failure must not block a send — the agent is trying to
 * report a problem, and refusing to carry their photo because a web worker died would be
 * a worse outcome than sending it uncompressed.
 */
export const compressForUpload = async (file: File): Promise<File> => {
	if (file.size <= COMPRESS_THRESHOLD_BYTES) return file;
	if (!file.type.startsWith('image/')) return file;

	try {
		const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
		// Compression can enlarge an already-optimised file. Keep whichever is smaller.
		return compressed.size < file.size ? compressed : file;
	} catch {
		return file;
	}
};

/**
 * True when compression actually helped, for the "1.2 → 0.4 MB" hint in the composer.
 * Agents deciding whether to walk toward better signal want the number.
 */
export const compressionSaving = (original: number, compressed: number): number =>
	original <= 0 ? 0 : Math.max(0, Math.round(((original - compressed) / original) * 100));

/**
 * Renders a tiny JPEG data URL for the outbox.
 *
 * The full blob is never persisted (see IOutboxAttachmentMeta), so this is what lets a
 * queued photo still draw its bubble after the tab reloads — the agent sees their message
 * with the image, greyed, and can re-pick the file against the same clientId.
 *
 * Returns undefined rather than throwing when canvas is unavailable, as it is in jsdom.
 */
export const buildThumbnailDataUrl = async (file: File): Promise<string | undefined> => {
	try {
		const compressed = await imageCompression(file, {
			maxWidthOrHeight: THUMBNAIL_MAX_PX,
			maxSizeMB: 0.01,
			useWebWorker: true,
		});
		return await imageCompression.getDataUrlFromFile(compressed);
	} catch {
		return undefined;
	}
};
